import {
  CounterQuestionConfig,
  CounterResponseState,
  CounterType,
  formatNumber,
} from '@gripp/shared-logic';
import { t } from 'i18next';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { routineMessageStyles } from '../../../components/routineMessageStyles';
import { RoutineQuestionMessage, RoutineQuestionMessageProps } from '../types';

// Helper to compute display values (shared by the three variants)
const computeDisplay = (
  question: CounterQuestionConfig,
  response: CounterResponseState
) => {
  const decimalPlaces = (question as any)?.decimalPlaces ?? 0;
  const isDefined = (v: any) =>
    v !== undefined && v !== null && !Number.isNaN(v);
  const showBaseline = question.counterType !== CounterType.Simple;

  const originalRaw = response.oldValue;
  const originalValue =
    showBaseline && isDefined(originalRaw)
      ? formatNumber(originalRaw as number, decimalPlaces, true)
      : undefined;

  const userRaw = response.userCount;
  const userValue =
    showBaseline && isDefined(userRaw) && (userRaw as number) !== 0
      ? formatNumber(Math.abs(userRaw as number), decimalPlaces, true)
      : undefined;

  const countRaw = response.count;
  const countValue = isDefined(countRaw)
    ? formatNumber(countRaw as number, decimalPlaces, true)
    : '--';

  let addedRemovedLabel: string | undefined;
  if (userValue) {
    if (isDefined(originalRaw) && isDefined(countRaw)) {
      addedRemovedLabel = t(
        `routines.message.${
          (originalRaw as number) > (countRaw as number) ? 'removed' : 'added'
        }`
      );
    } else if ((userRaw as number) > 0) {
      addedRemovedLabel = t('routines.message.added');
    } else if ((userRaw as number) < 0) {
      addedRemovedLabel = t('routines.message.removed');
    }
  }

  return {
    originalValue,
    userValue,
    addedRemovedLabel,
    countValue,
    unit: countValue === '--' ? '' : ` ${question.uom}`,
  } as const;
};

const CumulativeCounterMessage = (
  props: RoutineQuestionMessageProps<
    CounterQuestionConfig,
    CounterResponseState
  >
) => {
  const { question, response } = props;
  const d = computeDisplay(question, response);
  return (
    <View>
      {d.originalValue && (
        <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
          {t('routines.message.originalValue')}: {d.originalValue}{' '}
          {question.uom}
        </Text>
      )}
      {d.userValue && d.addedRemovedLabel && (
        <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
          {d.addedRemovedLabel}: {d.userValue} {question.uom}
        </Text>
      )}
      <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
        {t('routines.message.newValue')}: {d.countValue} {d.unit}
      </Text>
    </View>
  );
};

const TallyCounterMessage = CumulativeCounterMessage; // identical behaviour

const SimpleCounterMessage = (
  props: RoutineQuestionMessageProps<
    CounterQuestionConfig,
    CounterResponseState
  >
) => {
  const { question, response } = props;
  const d = computeDisplay(question, response); // still reuse for consistency
  return (
    <View>
      <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
        {d.countValue} {d.unit}
      </Text>
    </View>
  );
};

const ComponentMap: Record<CounterType, any> = {
  [CounterType.Cumulative]: CumulativeCounterMessage,
  [CounterType.Simple]: SimpleCounterMessage,
  [CounterType.Tally]: TallyCounterMessage,
};

export const CounterQuestionMessage: RoutineQuestionMessage<
  CounterQuestionConfig,
  CounterResponseState
> = (
  props: RoutineQuestionMessageProps<
    CounterQuestionConfig,
    CounterResponseState
  >
) => {
  const type = props.question.counterType ?? CounterType.Cumulative;
  const Comp = ComponentMap[type];
  return <Comp {...props} />;
};
